import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

export default function MapView({ origin, destination, route, stops = [], height = 220 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    if (!origin || !destination) return;
    
    mapRef.current = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [(origin.lng + destination.lng) / 2, (origin.lat + destination.lat) / 2],
      zoom: 6,
      attributionControl: false,
    });
    
    mapRef.current.on('load', () => {
      if (!mapRef.current) return;
      
      new mapboxgl.Marker({ color: '#20d9a6' })
        .setLngLat([origin.lng, origin.lat])
        .addTo(mapRef.current);
      
      new mapboxgl.Marker({ color: '#ff5757' })
        .setLngLat([destination.lng, destination.lat])
        .addTo(mapRef.current);
      
      stops.forEach((stop) => {
        if (!stop.latitude || !stop.longitude) return;
        new mapboxgl.Marker({ color: '#ffb84d' })
          .setLngLat([stop.longitude, stop.latitude])
          .addTo(mapRef.current);
      });
      
      if (route?.geometry_simplified?.length) {
        mapRef.current.addSource('route', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: route.geometry_simplified,
            },
          },
        });
        mapRef.current.addLayer({
          id: 'route-line',
          type: 'line',
          source: 'route',
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#20d9a6',
            'line-width': 3,
            'line-opacity': 0.85,
          },
        });
      }
      
      const lngs = [origin.lng, destination.lng, ...stops.map(s => s.longitude).filter(Boolean)];
      const lats = [origin.lat, destination.lat, ...stops.map(s => s.latitude).filter(Boolean)];
      const bounds = new mapboxgl.LngLatBounds(
        [Math.min(...lngs), Math.min(...lats)],
        [Math.max(...lngs), Math.max(...lats)]
      );
      mapRef.current.fitBounds(bounds, { padding: 40, duration: 500 });
    });
    
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [origin, destination, route, stops]);
  
  if (!import.meta.env.VITE_MAPBOX_TOKEN) {
    return (
      <div style={{
        height,
        background: 'var(--bg-card)',
        border: '0.5px solid var(--border-subtle)',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text-tertiary)',
        fontSize: 12,
        marginBottom: 12,
      }}>
        Map unavailable — VITE_MAPBOX_TOKEN missing
      </div>
    );
  }
  
  return (
    <div
      ref={containerRef}
      style={{
        height,
        borderRadius: 12,
        overflow: 'hidden',
        border: '0.5px solid var(--border-subtle)',
        marginBottom: 12,
      }}
    />
  );
}
